import {Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output} from '@angular/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {Address, Country, Customer} from '~core/models';
import {AddressService, StorageService} from '~core/services';
import {TooltipTranslationService} from '~shared/translations';

/**
 * Handles contact management.
 */
@UntilDestroy()
@Component({
  selector: 'app-address-list',
  templateUrl: 'address-list.component.html',
  styleUrls: ['../../../customer-overview.component.scss']
})
export class AddressListComponent implements OnInit, OnDestroy {
  public loading: boolean = true;
  public countries: Array<Country> = [];
  public user: Customer;
  public modalOpened: boolean = false;
  public currentAddresses: Array<Address> = [];

  @Input() public customer: Customer;
  @Input() public isPanelOpened: boolean = true;
  @Output() public isPanelOpenedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    public customerOverviewService: CustomerOverViewService,
    private addressService: AddressService,
    private storage: StorageService,
    public tooltipTranslations: TooltipTranslationService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.storage.castCurrentCountries.pipe(untilDestroyed(this))
      .subscribe(countries => this.countries = countries);
    this.addressService.castAddressList.pipe(untilDestroyed(this))
      .subscribe(data => this.currentAddresses = data);
  }

  /**
   * Create the form and fetch the countries and the current user
   */
  public ngOnInit(): any {
    this.addressService.setAddressList(this.customer.addresses);
  }

  public ngOnDestroy(): void {
  }

  /**
   * Open the create address modal and pass the customer to it.
   */
  public createAddress(): void {
    this.customerOverviewService.toggleAllModal(false);
    this.customerOverviewService.toggleCreateAddressesModal(true);
  }

  /**
   * Open the edit address modal and pass the address data to it.
   */
  public editAddress(address: Address): any {
    this.addressService.announceUpdatedAddress(address);
    this.customerOverviewService.toggleAllModal(false);
    this.customerOverviewService.toggleEditAddressesModal(true);
  }

  /**
   * Function that executes the delete address process
   */
  public deleteAddressModal(addressId: number): void {
    this.customerOverviewService.toggleDeleteModal(true, 'address', addressId);
  }

  /**
   * Delete an address.
   */
  public deleteAddress(addressId: number): any {
    this.customerOverviewService.toggleLoadingSubmitClicked(true);
    this.addressService.delete(addressId).pipe(untilDestroyed(this))
      .subscribe(() => {
        this.customerOverviewService.toggleLoadingSubmitClicked(false);
        this.customerOverviewService.toggleAllModal(false);
        this.customerOverviewService.toggleDeleteModal(false, '', null);
        this.addressService.deleteAddressFromList(addressId);
      });
  }

  /**
   * Returns the corect tooltip text to display on the street data
   */
  public streetTooltip(address: Address): any {
    if (this.customer.isCompany) {
      return this.customer.company;
    } else {
      const salutationT = address.salutation ? `${address.salutation.getTranslatedSalutation} ` : '';
      const addressT = address.title != null ? `${address.title} ` : '';

      return `${salutationT}${addressT}${address.firstName} ${address.lastName}`;
    }
  }
}
